import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { jobsAPI, categoriesAPI, locationsAPI, skillsAPI, companiesAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { JOB_TYPES, EXPERIENCE_LEVELS } from '../../utils/helpers';
import { FiArrowLeft } from 'react-icons/fi';
import toast from 'react-hot-toast';
import './Jobs.css';

const JobForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isEdit = !!id;
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState([]);
  const [locations, setLocations] = useState([]);

  const [form, setForm] = useState({
    title: '', description: '', requirements: '', benefits: '',
    job_type: 'full-time', experience_level: 'mid',
    salary_min: '', salary_max: '', location_id: '', category_id: '',
    is_remote: false, is_active: true, deadline: '', skills: '',
    company_id: '',
  });

  const [errors, setErrors] = useState({});
  const [companies, setCompanies] = useState([]);

  useEffect(() => {
    const load = async () => {
      try {
        const [catRes, locRes, compRes] = await Promise.allSettled([
          categoriesAPI.list(), locationsAPI.list(), companiesAPI.list(),
        ]);
        if (catRes.status === 'fulfilled') {
          const d = catRes.value.data;
          setCategories(d.data || d.categories || d.results || (Array.isArray(d) ? d : []));
        }
        if (locRes.status === 'fulfilled') {
          const d = locRes.value.data;
          setLocations(d.data || d.locations || d.results || (Array.isArray(d) ? d : []));
        }
        if (compRes.status === 'fulfilled') {
          const d = compRes.value.data;
          const compList = d.data || d.companies || d.results || (Array.isArray(d) ? d : []);
          setCompanies(compList);
          // Auto-set company_id from user's company or first available
          const userCompanyId = user?.company_id || user?.company?.id;
          if (userCompanyId) {
            setForm(prev => ({ ...prev, company_id: userCompanyId }));
          } else if (compList.length === 1) {
            setForm(prev => ({ ...prev, company_id: compList[0].id }));
          }
        }
      } catch (e) {}

      if (isEdit) {
        setLoading(true);
        try {
          const { data } = await jobsAPI.get(id);
          const job = data.data || data.job || data;
          setForm({
            title: job.title || job.name || '', description: job.description || '',
            requirements: job.requirements || '', benefits: job.benefits || '',
            job_type: job.job_type || 'full-time', experience_level: job.experience_level || 'mid',
            salary_min: job.salary_min || '', salary_max: job.salary_max || '',
            location_id: job.location_id || '', category_id: job.category_id || '',
            is_remote: job.is_remote || false, is_active: job.is_active !== false, deadline: job.deadline || '',
            skills: (job.skills || []).map(s => typeof s === 'string' ? s : s.name).join(', '),
            company_id: job.company_id || '',
          });
        } catch (err) {
          toast.error('Job not found');
          navigate('/dashboard');
        }
        setLoading(false);
      }
    };
    load();
  }, [id, isEdit, navigate, user]);

  const validate = () => {
    const errs = {};
    if (!form.title) errs.title = 'Title is required';
    if (!form.description) errs.description = 'Description is required';
    if (!form.company_id) errs.company_id = 'Company is required';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSaving(true);
    try {
      const payload = { ...form };
      if (payload.salary_min) payload.salary_min = parseInt(payload.salary_min);
      if (payload.salary_max) payload.salary_max = parseInt(payload.salary_max);
      if (payload.location_id) payload.location_id = parseInt(payload.location_id);
      if (payload.category_id) payload.category_id = parseInt(payload.category_id);
      if (payload.company_id) payload.company_id = parseInt(payload.company_id);
      // Convert skills string to array
      if (typeof payload.skills === 'string') {
        payload.skills = payload.skills.split(',').map(s => s.trim()).filter(Boolean);
      }
      // Backend expects 'name', frontend uses 'title' — send both
      if (payload.title) payload.name = payload.title;

      if (isEdit) {
        await jobsAPI.update(id, payload);
        toast.success('Job updated');
      } else {
        await jobsAPI.create(payload);
        toast.success('Job posted!');
      }
      navigate('/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save');
    }
    setSaving(false);
  };

  if (loading) return <div className="loading-container"><div className="spinner" /></div>;

  return (
    <div className="job-form-page">
      <div className="container" style={{ maxWidth: '800px' }}>
        <button onClick={() => navigate(-1)} className="back-link" style={{ background: 'none', color: 'var(--text-secondary)' }}>
          <FiArrowLeft /> Back
        </button>
        <div className="page-header">
          <h1>{isEdit ? 'Edit Job' : 'Post a New Job'}</h1>
          <p>{isEdit ? 'Update the job listing details' : 'Fill in the details to attract the best candidates'}</p>
        </div>

        <form onSubmit={handleSubmit} className="card" style={{ padding: '2rem' }}>
          <div className="form-group">
            <label>Job Title *</label>
            <input type="text" placeholder="e.g. Senior Software Developer" value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })} />
            {errors.title && <div className="error">{errors.title}</div>}
          </div>

          <div className="form-group">
            <label>Company *</label>
            {companies.length > 0 ? (
              <select value={form.company_id} onChange={(e) => setForm({ ...form, company_id: e.target.value })}>
                <option value="">Select company</option>
                {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            ) : (
              <div style={{ padding: '0.75rem', background: 'var(--bg-secondary, #f8f9fa)', borderRadius: '0.5rem', fontSize: '0.85rem' }}>
                No companies found. <a href="/dashboard" style={{ color: 'var(--accent-primary, #3b82f6)' }}>Create a company first</a> before posting a job.
              </div>
            )}
            {errors.company_id && <div className="error">{errors.company_id}</div>}
          </div>

          <div className="grid-2">
            <div className="form-group">
              <label>Job Type</label>
              <select value={form.job_type} onChange={(e) => setForm({ ...form, job_type: e.target.value })}>
                {JOB_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Experience Level</label>
              <select value={form.experience_level} onChange={(e) => setForm({ ...form, experience_level: e.target.value })}>
                {EXPERIENCE_LEVELS.map(l => <option key={l.value} value={l.value}>{l.label}</option>)}
              </select>
            </div>
          </div>

          <div className="form-group">
            <label>Description *</label>
            <textarea rows="6" placeholder="Describe the role, responsibilities, and what a typical day looks like..."
              value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            {errors.description && <div className="error">{errors.description}</div>}
          </div>

          <div className="form-group">
            <label>Requirements</label>
            <textarea rows="4" placeholder="Qualifications, skills, and experience required..."
              value={form.requirements} onChange={(e) => setForm({ ...form, requirements: e.target.value })} />
          </div>

          <div className="form-group">
            <label>Benefits</label>
            <textarea rows="3" placeholder="Perks, benefits, and what makes your company great..."
              value={form.benefits} onChange={(e) => setForm({ ...form, benefits: e.target.value })} />
          </div>

          <div className="grid-2">
            <div className="form-group">
              <label>Salary Min (ZAR)</label>
              <input type="number" placeholder="e.g. 400000" value={form.salary_min}
                onChange={(e) => setForm({ ...form, salary_min: e.target.value })} />
            </div>
            <div className="form-group">
              <label>Salary Max (ZAR)</label>
              <input type="number" placeholder="e.g. 700000" value={form.salary_max}
                onChange={(e) => setForm({ ...form, salary_max: e.target.value })} />
            </div>
          </div>

          <div className="grid-2">
            {locations.length > 0 && (
              <div className="form-group">
                <label>Location</label>
                <select value={form.location_id} onChange={(e) => setForm({ ...form, location_id: e.target.value })}>
                  <option value="">Select location</option>
                  {locations.map(l => <option key={l.id} value={l.id}>{l.name || l.city}</option>)}
                </select>
              </div>
            )}
            {categories.length > 0 && (
              <div className="form-group">
                <label>Category</label>
                <select value={form.category_id} onChange={(e) => setForm({ ...form, category_id: e.target.value })}>
                  <option value="">Select category</option>
                  {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
            )}
          </div>

          <div className="form-group">
            <label>Skills (comma-separated)</label>
            <input type="text" placeholder="e.g. Python, React, SQL, Docker" value={form.skills}
              onChange={(e) => setForm({ ...form, skills: e.target.value })} />
          </div>

          <div className="form-group">
            <label>Application Deadline</label>
            <input type="date" value={form.deadline} onChange={(e) => setForm({ ...form, deadline: e.target.value })} />
          </div>

          <div style={{ display: 'flex', gap: '1.5rem', margin: '0.5rem 0 1rem' }}>
            <label className="checkbox-label"><input type="checkbox" checked={form.is_remote} onChange={(e) => setForm({ ...form, is_remote: e.target.checked })} /> Remote position</label>
            <label className="checkbox-label"><input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} /> Active (visible to candidates)</label>
          </div>

          <div style={{ display: 'flex', gap: '0.75rem' }}>
            <button type="submit" className="btn btn-primary btn-lg" disabled={saving} style={{ flex: 2 }}>
              {saving ? 'Saving...' : (isEdit ? 'Update Job' : 'Post Job')}
            </button>
            <button type="button" className="btn btn-secondary btn-lg" onClick={() => navigate(-1)} style={{ flex: 1 }}>Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default JobForm;
